import Button from 'tio-reimagined/components/button';
import Form from 'tio-reimagined/components/form';
import { Input, RadioGroup } from 'tio-ui/components/forms';
import ReSelect from 'tio-reimagined/components/select';
import { or } from 'tio-ui/utilities';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import * as v from 'valibot';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { VStack, Section } from 'tio-ui/components/layout';
import Text from 'tio-reimagined/components/text';
import Component from '@glimmer/component';
import embedCalendly from 'tio-common/modifiers/embed-calendly';
import ENV from 'tio-employee/config/environment';
import TioAlert from 'tio-common/components/tio/alert';
import transitionTo from 'ember-router-helpers/helpers/transition-to';
import type IntlService from 'ember-intl/services/intl';
import type SessionContextService from 'tio-employee/services/session-context';
import { timeZone } from 'tio-common/utils/date/time-zone';
import { generateRequestId } from 'tio-common/utils/request-id';

const paymentPlans = [
  '10 year standard repayment plan',
  'Graduated',
  'Extended',
  'Pay As You Earn (PAYE)',
  'SAVE (formerly REPAYE)',
  'Income-Based Repayment (IBR)',
  'Income-Contingent Repayment',
  'Other',
];

// Base schema for common fields
const baseSchema = v.object({
  topic: v.pipe(v.string('Required'), v.trim(), v.nonEmpty('Please enter a topic')),
  repaymentGoal: v.pipe(v.string('Required'), v.nonEmpty('Please select a repayment goal')),
  studentLoanType: v.pipe(v.string('Required'), v.nonEmpty('Please select a loan type')),
  repaymentPlan: v.pipe(
    v.array(v.string('Required')),
    v.minLength(1, 'Please select at least one repayment plan')
  ),
  monthlyPayment: v.pipe(v.string('Required'), v.nonEmpty('Please select an option')),
  sessionType: v.pipe(v.string('Required'), v.nonEmpty('Please select a session type')),
  email: v.optional(v.string()),
});

// Schema with email validation for email session type
const emailSessionSchema = v.pipe(
  baseSchema,
  v.forward(
    v.partialCheck(
      [['sessionType'], ['email']],
      (input) => {
        if (input.sessionType === 'email') {
          return !!input.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.email);
        }
        return true;
      },
      'Please enter a valid email address'
    ),
    ['email']
  )
);

type FormSchema = v.InferOutput<typeof baseSchema>;

class CoachSchedulerRoute extends Component {
  @service declare sessionContext: SessionContextService;
  @service declare intl: IntlService;

  @tracked showSchedulingCalendar = false;
  @tracked submissionError = false;
  @tracked calendlyError = false;
  @tracked didSubmitSuccessfully = false;
  @tracked formData: FormSchema = {
    topic: '',
    repaymentGoal: '',
    studentLoanType: '',
    repaymentPlan: [],
    monthlyPayment: '',
    sessionType: '',
    email: '',
  };

  get schema() {
    // Use schema with email validation when email session is selected
    return this.formData.sessionType === 'email' ? emailSessionSchema : baseSchema;
  }

  get plainText() {
    const answers = {
      scheduling_question_topic: this.formData.topic,
      scheduling_question_repayment_goal: this.formData.repaymentGoal,
      scheduling_question_loan_type: this.formData.studentLoanType,
      scheduling_question_repayment_plan: this.formData.repaymentPlan?.join(', '),
      scheduling_question_can_afford: this.formData.monthlyPayment,
      scheduling_question_session_kind: this.formData.sessionType,
      first_name: this.sessionContext.user?.person.firstName,
      last_name: this.sessionContext.user?.person.lastName,
      employee_id: this.sessionContext.currentEmployee?.id,
      user_id: this.sessionContext.user?.id,
      email: this.formData.email,
    };

    return Object.entries(answers)
      .filter(([_, answer]) => answer)
      .map(([question, answer]) => `${question}: ${answer}.`)
      .join('\n');
  }

  get calendlyPrefillData() {
    return {
      firstName: this.sessionContext.user?.person.firstName,
      lastName: this.sessionContext.user?.person.lastName,
    };
  }

  get emailPreferred() {
    return this.formData.sessionType === 'email';
  }

  validate = (data: FormSchema) => {
    const r = v.safeParse(this.schema, data);
    return r.success ? {} : (v.flatten(r.issues).nested ?? {});
  };

  updateRepaymentPlan = (keys: string[]) => {
    this.formData = { ...this.formData, repaymentPlan: keys };
  };

  onCalendlyError = () => {
    this.calendlyError = true;
  };

  handleFormChange = (result: { data: FormSchema }) => {
    this.formData = result.data;
  };

  submit = dropTask(async () => {
    this.submissionError = false;

    const headers = {
      'x-api-key': ENV.apiKey,
      'tio-auth-token': this.sessionContext.session.data.authenticated?.access_token || '',
      'Content-Type': 'application/json',
      'Time-Zone': timeZone(),
      'X-Request-Id': generateRequestId(),
    };

    const url = `${ENV.apiHost}/coaching/schedule-loan`;
    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify({
        scheduleRequestContent: this.plainText,
      }),
      headers,
    });

    if (!response.ok) {
      this.submissionError = true;
      return;
    }

    // Scheduling is only needed for a phone session
    if (!this.emailPreferred) {
      this.showSchedulingCalendar = true;
    } else {
      this.didSubmitSuccessfully = true;
    }
  });

  <template>
    <VStack>
      <Section>
        <:body>
          {{#unless (or this.showSchedulingCalendar this.didSubmitSuccessfully)}}
            <Form
              @data={{this.formData}}
              @validate={{this.validate}}
              @onChange={{this.handleFormChange}}
              @onSubmit={{this.submit.perform}}
              as |form|
            >
              <VStack>
                <Text @tag="p">{{t "coaching.schedule_time.answer_questions"}}</Text>

                {{! Topic question section }}
                <VStack @collapsed={{true}}>
                  {{#let (form.field "topic") as |f|}}
                    <Input
                      @label={{t "coaching.schedule_time.speak_about"}}
                      @isRequired={{true}}
                      @value={{f.value}}
                      @onChange={{f.onChange}}
                      @onBlur={{f.onBlur}}
                      @errors={{f.errors}}
                    />
                  {{/let}}
                  <Text @tag="p" @variant="muted" @size="sm">
                    {{t "coaching.schedule_time.be_detailed"}}
                  </Text>
                </VStack>

                {{! Repayment goal question section }}
                {{#let (form.field "repaymentGoal") as |f|}}
                  <RadioGroup
                    @label={{t "coaching.schedule_time.what_goal"}}
                    @isRequired={{true}}
                    @value={{f.value}}
                    @onChange={{f.onChange}}
                    @errors={{f.errors}}
                    as |Radio|
                  >
                    <Radio
                      @value={{t "coaching.schedule_time.goal_choice_1"}}
                      @label={{t "coaching.schedule_time.goal_choice_1"}}
                    />
                    <Radio
                      @value={{t "coaching.schedule_time.goal_choice_2"}}
                      @label={{t "coaching.schedule_time.goal_choice_2"}}
                    />
                  </RadioGroup>
                {{/let}}

                {{! Loan types question section }}
                {{#let (form.field "studentLoanType") as |f|}}
                  <RadioGroup
                    @label={{t "coaching.schedule_time.what_have_you"}}
                    @isRequired={{true}}
                    @value={{f.value}}
                    @onChange={{f.onChange}}
                    @errors={{f.errors}}
                    as |Radio|
                  >
                    <Radio
                      @value={{t "coaching.schedule_time.have_choice_1"}}
                      @label={{t "coaching.schedule_time.have_choice_1"}}
                    />
                    <Radio
                      @value={{t "coaching.schedule_time.have_choice_2"}}
                      @label={{t "coaching.schedule_time.have_choice_2"}}
                    />
                    <Radio
                      @value={{t "coaching.schedule_time.have_choice_3"}}
                      @label={{t "coaching.schedule_time.have_choice_3"}}
                    />
                    <Radio
                      @value={{t "coaching.schedule_time.have_choice_4"}}
                      @label={{t "coaching.schedule_time.have_choice_4"}}
                    />
                  </RadioGroup>
                {{/let}}

                {{! Payment plans question section }}
                {{#let (form.field "repaymentPlan") as |f|}}
                  <ReSelect
                    @label={{t "coaching.schedule_time.current_plan"}}
                    @multiple={{true}}
                    @selectedKeys={{this.formData.repaymentPlan}}
                    @onChangeKeys={{this.updateRepaymentPlan}}
                    @items={{paymentPlans}}
                    @clearable={{true}}
                    @placeholder="Payment Method"
                    {{! @glint-expect-error: @isRequired not yet supported on reimagined Select }}
                    @isRequired={{true}}
                    @errors={{f.errors}}
                  />
                {{/let}}

                {{! Can afford question section }}
                {{#let (form.field "monthlyPayment") as |f|}}
                  <RadioGroup
                    @label={{t "coaching.schedule_time.can_you_afford"}}
                    @isRequired={{true}}
                    @value={{f.value}}
                    @onChange={{f.onChange}}
                    @errors={{f.errors}}
                    as |Radio|
                  >
                    <Radio
                      @value={{t "coaching.schedule_time.yes"}}
                      @label={{t "coaching.schedule_time.yes"}}
                    />
                    <Radio
                      @value={{t "coaching.schedule_time.no"}}
                      @label={{t "coaching.schedule_time.no"}}
                    />
                  </RadioGroup>
                {{/let}}

                {{! Session kind question section }}
                {{#let (form.field "sessionType") as |f|}}
                  <RadioGroup
                    @label={{t "coaching.schedule_time.kind_of_session"}}
                    @isRequired={{true}}
                    @value={{f.value}}
                    @onChange={{f.onChange}}
                    @errors={{f.errors}}
                    as |Radio|
                  >
                    <Radio @value="phone" @label={{t "coaching.schedule_time.phone"}} />
                    <Radio @value="email" @label={{t "coaching.schedule_time.email"}} />
                  </RadioGroup>
                {{/let}}

                {{#if this.emailPreferred}}
                  {{! Email prompt section }}
                  {{#let (form.field "email") as |f|}}
                    <Input
                      @label={{t "coaching.schedule_time.enter_email"}}
                      @type="email"
                      @isRequired={{true}}
                      @value={{f.value}}
                      @onChange={{f.onChange}}
                      @onBlur={{f.onBlur}}
                      @errors={{f.errors}}
                    />
                  {{/let}}
                {{/if}}

                <div class="flex gap-x-4 pt-4 border-t border-gray-200">
                  <Button
                    @appearance="minimal"
                    {{on
                      "click"
                      (transitionTo "authenticated.repaying-student-debt.coaching.index")
                    }}
                  >
                    {{t "common.cancel"}}
                  </Button>
                  <Button @intent="primary" @isRunning={{this.submit.isRunning}} type="submit">
                    {{t "common.submit"}}
                  </Button>
                </div>
              </VStack>
            </Form>
          {{/unless}}

          {{#if this.showSchedulingCalendar}}
            {{#if this.calendlyError}}
              <TioAlert @type="error" @allowDismiss={{false}}>
                <:body>
                  <Text class="font-bold">{{t "coaching.schedule_time.calendly_error"}}</Text>
                </:body>
              </TioAlert>
            {{/if}}
            <div
              id="calendly-container"
              class="h-189"
              {{embedCalendly
                "https://calendly.com/tiocoaches"
                this.calendlyPrefillData
                this.onCalendlyError
              }}
            />
          {{/if}}

          {{#if this.submissionError}}
            <TioAlert @type="error" @allowDismiss={{false}} class="mb-6">
              <:body>
                <Text class="font-bold">{{t "coaching.schedule_time.submission_error"}}</Text>
              </:body>
            </TioAlert>
          {{/if}}

          {{#if this.didSubmitSuccessfully}}
            <TioAlert @type="success" @allowDismiss={{false}}>
              <:body>
                <Text class="font-bold">{{t "coaching.schedule_time.submission_success"}}</Text>
              </:body>
            </TioAlert>
          {{/if}}
        </:body>
      </Section>
    </VStack>
  </template>
}

export default CoachSchedulerRoute;
